/**
 * Decides whether an audit upload actually landed on the server after the
 * upload request itself died. Long transfers through dealership networks can
 * lose the success reply AFTER the file has been saved — an ~85 MB Tekion
 * export did exactly that on 2026-09-02, and navigating away and back was the
 * only way to find out it had worked. Pure so it's testable: the caller
 * fetches the group's uploaded-files list before the upload and again after
 * the failure, and this compares the two.
 */
import { detectVendorFromFilename } from "./vendorDetection"

/** Structural subset of api/audit's AuditUploadedFile — just what the
 *  comparison needs, so callers can pass the API rows straight through. */
export interface UploadedFileStamp {
  filename: string
  vendor: string
  modified: string
}

/**
 * True when a file of the SAME vendor as the local file is present after the
 * failure but wasn't before. Saved files get standardized timestamped names,
 * so both a brand-new file and a same-second replacement show up as a
 * (filename, modified) pair the before-list didn't have. Vendor-matching keeps
 * another operator's unrelated upload (a HomeNet file landing while our Tekion
 * died) from reading as our success. Compares server stamps to server stamps —
 * never to the browser clock, which can skew.
 */
export function uploadLanded(
  localFilename: string,
  before: UploadedFileStamp[],
  after: UploadedFileStamp[],
): boolean {
  const vendor = detectVendorFromFilename(localFilename)
  if (vendor === "unknown") return false
  const seen = new Set(before.map((f) => `${f.filename}|${f.modified}`))
  return after.some((f) => f.vendor === vendor && !seen.has(`${f.filename}|${f.modified}`))
}
